/**
 * The contract between the dashboard's data layer and everything that draws.
 *
 * Fleet data arrives in tiers (see the snapshots bucket, `fleet/`): a small
 * core file for every client first, then heavier tier files a view asks for
 * when it needs them. Nothing is cut in any tier: a client's fragments from
 * every tier join back into its full snapshot.
 */
import type { InactiveTag } from '@/types';
import type { ClientSnapshot } from '@/types/fleetAnalytics';

/** A tier file under `fleet/` in the snapshots bucket. `core` is always loaded. */
export type FleetTier = 'users' | 'agents' | 'services' | 'inventory' | 'apps' | 'conversations';

export type TierState = 'idle' | 'loading' | 'ready' | 'error';

/** How the nightly gather went for one client. */
export interface ClientStatus {
  ok: boolean;
  lastAttemptAt?: string;
  lastOkAt?: string;
  /** Why the last gather failed. Empty when it did not. */
  lastError?: string;
}

/** A snapshot older than this is called stale on screen. The gather runs nightly. */
export const STALE_AFTER_HOURS = 36;

export interface Freshness {
  /** The snapshot's own time. */
  generatedAt?: string;
  ageHours: number | null;
  stale: boolean;
  /** The last gather failed. The data shown is the last good gather. */
  failed: boolean;
  error?: string;
}

export interface FleetData {
  /** True until the core data is on screen. */
  loading: boolean;
  error: string | null;
  /** `index`: the tiered S3 index. `dynamodb`: the old header rows, used only when the index does not exist yet. */
  source: 'index' | 'dynamodb' | null;
  /** When the index was built. */
  indexGeneratedAt?: string;
  /** One row per client stack and per infrastructure account. Each grows as tiers merge in. */
  snapshots: ClientSnapshot[];
  status: Record<string, ClientStatus>;
  freshness: (clientName: string) => Freshness;
  /** The stale or failed stacks among `clients`, oldest first. */
  staleAmong: (clientNames: string[]) => Array<{ clientName: string } & Freshness>;

  tiers: Record<FleetTier, TierState>;
  /** Ask for a tier. Safe to call on every render: it loads once. */
  ensureTier: (tier: FleetTier) => void;
  /** True when every named tier is merged into `snapshots`. */
  hasTiers: (...tiers: FleetTier[]) => boolean;

  /** One client's complete snapshot, when it is loaded. A single-client view uses this. */
  fullSnapshot: (clientName: string) => ClientSnapshot | undefined;
  ensureFull: (clientName: string) => void;
  fullLoading: (clientName: string) => boolean;

  reload: () => void;
}

// ── Sidebar ────────────────────────────────────────────────────────────────

/** A fleet-wide view: a named set of stacks summed together. */
export interface SidebarView {
  /** `_CLIENTS`, `_NEXTGEN`, `_STANDALONE`, `_NOLIA`, `_PARTNER:<partner>`, `_ARCANUM`, `_ARCANUM_INFRA`, `_FLEET`. */
  id: string;
  label: string;
  /** One sentence on exactly which stacks the view sums. */
  description: string;
  /** The stacks or accounts the view lists. Inactive stacks it hides are not counted. */
  count: number;
  unit: 'stacks' | 'accounts';
  group: 'customers' | 'partners' | 'arcanum';
}

/** One stack or one infrastructure account in the sidebar list. */
export interface SidebarStack {
  /** The selection id: the client name, or `_infra:<alias>`. */
  name: string;
  /** What a person reads: the client name, or the account alias with no prefix. */
  label: string;
  kind: 'customer' | 'dev' | 'internal' | 'infra';
  /** Spend in the selected window, USD. Null while the data loads. */
  spendUsd: number | null;
  /** Conversations in the selected window. Null for an infrastructure account. */
  conversations: number | null;
  generatedAt?: string;
  stale: boolean;
  failed: boolean;
  error?: string;
  /** The client carries the Inactive tag. */
  inactive?: boolean;
  /** The Inactive tag itself, for its hover: who set it, when and why. */
  inactiveTag?: InactiveTag;
  /** The contract badge beside the name. Null or absent when none is set, and for an infrastructure account. */
  contract?: { label: string; variant: string; hint?: string } | null;
}

export interface ClientSidebarProps {
  loading: boolean;
  views: SidebarView[];
  stacks: SidebarStack[];
  selected: string | null;
  onSelect: (id: string) => void;
  /** The selected window in words, shown above the list: "Last 30 days". */
  windowLabel: string;
  /** How many clients carry the Inactive tag. The switch below shows only when there is one. */
  inactiveCount: number;
  /** True when Inactive clients are in the list and in every view's use and credit figures. */
  showInactive: boolean;
  onShowInactive: (show: boolean) => void;
}
